import React, { useState, useEffect, useRef } from "react";
import { FiMapPin, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX } from "react-icons/fi";
import Select from 'react-select';
import api from "../../api";
import "./Card.css";

const ONEboxManager = ({ showToast, currentUser }) => {
  const [units, setUnits] = useState([]);
  const [myForms, setMyForms] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  
  // Registration form state
  const [unitCode, setUnitCode] = useState("");
  const [selectedFormId, setSelectedFormId] = useState("");
  const [locationName, setLocationName] = useState("");
  const [notes, setNotes] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [locationStatus, setLocationStatus] = useState("idle"); // idle, capturing, success, error
  const [locationError, setLocationError] = useState(null);
  
  // Map state
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState([37.7749, -122.4194]); // Default to SF
  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [markerInstance, setMarkerInstance] = useState(null);

  // Search and edit state
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  
  // View state
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [overviewMapInstance, setOverviewMapInstance] = useState(null);
  const overviewMapRef = useRef(null);

  // Computed values (MUST be here before any functions/hooks that use them)
  const partnerOptions = allPartners.map(partner => ({
    value: partner.id,
    label: partner.name
  }));

  const selectedPartnerOptions = partnerOptions.filter(option => 
    selectedPartnerIds.includes(option.value)
  );

  const filteredUnits = units.filter(unit => 
    unit.unitCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    unit.locationName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    unit.formOrgName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    loadData();
    loadPartners();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [unitsRes, formsRes] = await Promise.all([
        api.get("/qr/onebox/my-units"),
        api.get("/qr/my-forms")
      ]);
      setUnits(unitsRes.data || []);
      setMyForms(formsRes.data || []);
    } catch (err) {
      console.error("Error loading data:", err);
      showToast && showToast("Failed to load data", "error");
    }
    setLoading(false);
  };

  const loadPartners = async () => {
    try {
      const res = await api.get("/partner/admin/partners");
      setAllPartners(res.data || []);
    } catch (err) {
      console.error("Error loading partners:", err);
    }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported by browser");
      setLocationStatus("error");
      showToast && showToast("Geolocation not supported", "error");
      return;
    }

    setLocationStatus("capturing");
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        setMapCenter([lat, lng]);
        setLocationStatus("success");
        showToast && showToast(`Location captured (accuracy: ${Math.round(position.coords.accuracy)}m)`, "success");
        
        // Update map if open
        if (mapInstance && markerInstance) {
          mapInstance.setView([lat, lng], 16);
          markerInstance.setLatLng([lat, lng]);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLocationError(error.message);
        setLocationStatus("error");
        showToast && showToast(`Location error: ${error.message}`, "error");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Load Leaflet map when toggled
  useEffect(() => {
    if (showMap && !mapInstance) {
      // Dynamically load Leaflet CSS and JS
      const loadLeaflet = async () => {
        // Check if Leaflet is already loaded
        if (window.L) {
          initializeMap();
          return;
        }

        // Load Leaflet CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        // Load Leaflet JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
          initializeMap();
        };
        document.head.appendChild(script);
      };

      loadLeaflet();
    }
  }, [showMap]);

  const initializeMap = () => {
    if (!mapRef.current || mapInstance) return;

    const L = window.L;
    const lat = parseFloat(latitude) || mapCenter[0];
    const lng = parseFloat(longitude) || mapCenter[1];

    const map = L.map(mapRef.current).setView([lat, lng], 16);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    const marker = L.marker([lat, lng], { draggable: true }).addTo(map);
    
    marker.on('dragend', function(e) {
      const position = marker.getLatLng();
      setLatitude(position.lat.toFixed(6));
      setLongitude(position.lng.toFixed(6));
    });

    map.on('click', function(e) {
      marker.setLatLng(e.latlng);
      setLatitude(e.latlng.lat.toFixed(6));
      setLongitude(e.latlng.lng.toFixed(6));
    });

    setMapInstance(map);
    setMarkerInstance(marker);
  };

  // Initialize overview map when switching to map view
  useEffect(() => {
    if (viewMode === "map" && filteredUnits.length > 0) {
      // Cleanup existing map before recreating
      if (overviewMapInstance) {
        overviewMapInstance.remove();
        setOverviewMapInstance(null);
      }

      // Dynamically load Leaflet if needed
      const loadLeafletAndInitMap = async () => {
        if (window.L) {
          // Small delay to ensure ref is ready
          setTimeout(() => initializeOverviewMap(), 100);
          return;
        }

        // Load Leaflet CSS
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }

        // Load Leaflet JS
        if (!document.querySelector('script[src*="leaflet.js"]')) {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = () => {
            setTimeout(() => initializeOverviewMap(), 100);
          };
          document.head.appendChild(script);
        }
      };

      loadLeafletAndInitMap();
    }
  }, [viewMode, filteredUnits.length, units]);

  const initializeOverviewMap = () => {
    if (!overviewMapRef.current) return;
    if (filteredUnits.length === 0) return;

    const L = window.L;
    
    // Make sure the container is clean
    if (overviewMapRef.current._leaflet_id) {
      console.warn("Map container already initialized, cleaning up...");
      overviewMapRef.current.innerHTML = '';
      delete overviewMapRef.current._leaflet_id;
    }

    // Calculate center from all units
    const latitudes = filteredUnits.map(u => parseFloat(u.latitude));
    const longitudes = filteredUnits.map(u => parseFloat(u.longitude));
    const centerLat = latitudes.reduce((a, b) => a + b, 0) / latitudes.length;
    const centerLng = longitudes.reduce((a, b) => a + b, 0) / longitudes.length;

    try {
      const map = L.map(overviewMapRef.current).setView([centerLat, centerLng], 12);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    // Create custom icons
    const purpleIcon = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="background-color: #8b5cf6; width: 24px; height: 24px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"><div style="transform: rotate(45deg);"></div></div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28]
    });

    const greyIcon = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="background-color: #9ca3af; width: 24px; height: 24px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"><div style="transform: rotate(45deg);"></div></div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28]
    });

    // Add markers for each unit
    filteredUnits.forEach(unit => {
      const icon = unit.isActive ? purpleIcon : greyIcon;
      const marker = L.marker([parseFloat(unit.latitude), parseFloat(unit.longitude)], { icon }).addTo(map);
      
      // Create popup content
      const popupContent = `
        <div style="padding: 8px; min-width: 200px;">
          <h4 style="margin: 0 0 8px 0; font-weight: bold;">${unit.unitCode}</h4>
          ${unit.locationName ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Location:</strong> ${unit.locationName}</p>` : ''}
          ${unit.formOrgName ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Form:</strong> ${unit.formOrgName}</p>` : ''}
          <p style="margin: 4px 0; font-size: 13px;"><strong>Status:</strong> <span style="color: ${unit.isActive ? '#8b5cf6' : '#9ca3af'}; font-weight: bold;">${unit.isActive ? 'Active' : 'Inactive'}</span></p>
          ${unit.linkedPartners && unit.linkedPartners.length > 0 ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Partners:</strong> ${unit.linkedPartners.map(p => p.name).join(', ')}</p>` : ''}
          <p style="margin: 8px 0 0 0; font-size: 12px; color: #666;">${unit.latitude.toFixed(6)}, ${unit.longitude.toFixed(6)}</p>
        </div>
      `;
      
      marker.bindPopup(popupContent);
      
      // Open in Google Maps on marker click
      marker.on('click', () => {
        marker.openPopup();
      });
    });

      // Fit map to show all markers
      const bounds = L.latLngBounds(filteredUnits.map(u => [parseFloat(u.latitude), parseFloat(u.longitude)]));
      map.fitBounds(bounds, { padding: [50, 50] });

      setOverviewMapInstance(map);
    } catch (error) {
      console.error("Error initializing overview map:", error);
      // Clean up on error
      if (overviewMapRef.current) {
        overviewMapRef.current.innerHTML = '';
        delete overviewMapRef.current._leaflet_id;
      }
    }
  };

  // Cleanup overview map when component unmounts or view changes
  useEffect(() => {
    return () => {
      if (overviewMapInstance) {
        try {
          overviewMapInstance.remove();
        } catch (e) {
          console.warn("Error cleaning up map:", e);
        }
      }
      // Clean up the container
      if (overviewMapRef.current) {
        overviewMapRef.current.innerHTML = '';
        delete overviewMapRef.current._leaflet_id;
      }
    };
  }, [overviewMapInstance]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!unitCode || !latitude || !longitude) {
      showToast && showToast("Unit code and location are required", "error");
      return;
    }

    setRegistering(true);
    try {
      await api.post("/qr/onebox/register", {
        unitCode,
        formId: selectedFormId || null,
        locationName,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        notes
      });

      showToast && showToast("ONEbox unit registered successfully", "success");
      
      // Reset form
      setUnitCode("");
      setSelectedFormId("");
      setLocationName("");
      setLatitude("");
      setLongitude("");
      setNotes("");
      setLocationStatus("idle");
      setShowMap(false);
      
      // Reload units
      await loadData();
    } catch (err) {
      console.error("Error registering unit:", err);
      const errorMsg = err.response?.data?.error || "Failed to register unit";
      showToast && showToast(errorMsg, "error");
    }
    setRegistering(false);
  };

  const handleDelete = async (unitCode) => {
    if (!window.confirm(`Delete ONEbox unit ${unitCode}?`)) return;

    try {
      await api.delete(`/qr/onebox/${unitCode}`);
      showToast && showToast("Unit deleted", "success");
      await loadData();
    } catch (err) {
      console.error("Error deleting unit:", err);
      showToast && showToast("Failed to delete unit", "error");
    }
  };

  const handleUpdate = async (unit) => {
    try {
      // Update unit details
      await api.put(`/qr/onebox/${unit.unitCode}`, {
        formId: unit.formId,
        locationName: unit.locationName,
        latitude: parseFloat(unit.latitude),
        longitude: parseFloat(unit.longitude),
        notes: unit.notes,
        isActive: unit.isActive ?? true
      });

      // Update partner links
      await api.put(`/qr/onebox/${unit.unitCode}/partners`, {
        partnerIds: selectedPartnerIds
      });

      showToast && showToast("Unit updated successfully", "success");
      setEditingUnit(null);
      setSelectedPartnerIds([]);
      await loadData();
    } catch (err) {
      console.error("Error updating unit:", err);
      const errorMsg = err.response?.data?.error || "Failed to update unit";
      showToast && showToast(errorMsg, "error");
    }
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    // Set selected partner IDs from linkedPartners
    const partnerIds = unit.linkedPartners ? unit.linkedPartners.map(p => p.id) : [];
    setSelectedPartnerIds(partnerIds);
  };

  const handlePartnerSelectChange = (selectedOptions) => {
    const partnerIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedPartnerIds(partnerIds);
  };

  const openInMaps = (lat, lng, name) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 className="bold" style={{ marginBottom: 16 }}>ONEbox Unit Manager</h2>
      
      {/* Registration Form */}
      <div style={{ marginBottom: 32, padding: 16, border: "1px solid var(--border)", borderRadius: 8, background: "var(--card-bg)" }}>
        <h3 className="bold" style={{ marginBottom: 12 }}>Register New ONEbox Unit</h3>
        
        <form onSubmit={handleRegister}>
          <div className="form-group">
            <label>Unit Code *</label>
            <input
              type="text"
              value={unitCode}
              onChange={(e) => setUnitCode(e.target.value)}
              placeholder="e.g., ONEBOX-001"
              required
            />
            <small style={{ fontSize: 12, opacity: 0.7 }}>Unique identifier printed on the physical unit</small>
          </div>

          <div className="form-group">
            <label>Assign to QR Form (Optional)</label>
            <select
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
            >
              <option value="">-- No form assigned --</option>
              {myForms.map(form => (
                <option key={form.id} value={form.id}>
                  {form.orgName || `Form #${form.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Location Name</label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g., Main Entrance, Conference Room A"
            />
          </div>

          <div className="form-group">
            <label>Location Coordinates *</label>
            <div style={{ marginBottom: 8 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={captureLocation}
                disabled={locationStatus === "capturing"}
                style={{ marginRight: 8 }}
              >
                <FiMapPin size={16} style={{ marginRight: 4 }} />
                {locationStatus === "capturing" ? "Capturing..." : "Capture My Location"}
              </button>
              
              <button
                type="button"
                className="btn"
                onClick={() => setShowMap(!showMap)}
              >
                {showMap ? "Hide Map" : "Show Map"} 📍
              </button>
            </div>

            {locationStatus === "success" && (
              <div style={{ 
                padding: 8, 
                background: "rgba(40, 167, 69, 0.1)", 
                border: "1px solid rgba(40, 167, 69, 0.3)",
                borderRadius: 4, 
                fontSize: 12, 
                color: "#28a745", 
                marginBottom: 8 
              }}>
                ✓ Location captured successfully
              </div>
            )}

            {locationStatus === "error" && locationError && (
              <div style={{ 
                padding: 8, 
                background: "rgba(220, 53, 69, 0.1)", 
                border: "1px solid rgba(220, 53, 69, 0.3)",
                borderRadius: 4, 
                fontSize: 12, 
                color: "#dc3545", 
                marginBottom: 8 
              }}>
                ⚠ {locationError}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 12 }}>Latitude</label>
                <input
                  type="number"
                  step="0.000001"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="37.7749"
                  required
                />
              </div>
              <div>
                <label style={{ fontSize: 12 }}>Longitude</label>
                <input
                  type="number"
                  step="0.000001"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="-122.4194"
                  required
                />
              </div>
            </div>
            <small style={{ fontSize: 12, opacity: 0.7 }}>
              Click "Capture My Location" or manually enter coordinates. Use map below to fine-tune pin location.
            </small>
          </div>

          {showMap && (
            <div style={{ marginBottom: 16 }}>
              <div
                ref={mapRef}
                style={{
                  height: 400,
                  width: "100%",
                  borderRadius: 8,
                  border: "1px solid var(--border)"
                }}
              />
              <small style={{ fontSize: 12, opacity: 0.7, display: "block", marginTop: 4 }}>
                Drag the marker or click on the map to set the precise location
              </small>
            </div>
          )}

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional information about this unit..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-success"
              disabled={registering || !unitCode || !latitude || !longitude}
            >
              {registering ? "Registering..." : "Register Unit"}
            </button>
          </div>
        </form>
      </div>

      {/* Registered Units List */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 className="bold" style={{ margin: 0 }}>
            Registered Units ({filteredUnits.length}{units.length !== filteredUnits.length ? ` / ${units.length}` : ''})
          </h3>
        </div>

        {/* View Tabs */}
        {units.length > 0 && (
          <div className="onebox-view-tabs" style={{ 
            display: 'flex', 
            gap: '0.75rem', 
            marginBottom: '1.5rem', 
            borderBottom: '2px solid var(--border)', 
            paddingBottom: '0.75rem' 
          }}>
            <button 
              className={`tab-button ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              style={{
                flex: 1,
                justifyContent: 'center',
                padding: '0.5rem 1rem',
                backgroundColor: viewMode === "list" ? 'var(--primary)' : 'var(--card-bg)',
                color: viewMode === "list" ? 'var(--button-text)' : 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '6px 6px 0 0',
                borderBottom: viewMode === "list" ? '3px solid var(--primary)' : '3px solid transparent',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              List View
            </button>
            <button 
              className={`tab-button ${viewMode === "map" ? "active" : ""}`}
              onClick={() => setViewMode("map")}
              style={{
                flex: 1,
                justifyContent: 'center',
                padding: '0.5rem 1rem',
                backgroundColor: viewMode === "map" ? 'var(--primary)' : 'var(--card-bg)',
                color: viewMode === "map" ? 'var(--button-text)' : 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '6px 6px 0 0',
                borderBottom: viewMode === "map" ? '3px solid var(--primary)' : '3px solid transparent',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <FiMapPin style={{ marginRight: 4, display: 'inline' }} size={16} />
              Map View
            </button>
          </div>
        )}

        {/* Search Bar - Only show in list view */}
        {units.length > 0 && viewMode === "list" && (
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search units by code, location, or form..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                background: 'var(--input-bg)',
                color: 'var(--input-text)',
                fontSize: '14px'
              }}
            />
          </div>
        )}
        
        {loading ? (
          <p>Loading units...</p>
        ) : units.length === 0 ? (
          <p style={{ opacity: 0.7 }}>No units registered yet.</p>
        ) : viewMode === "map" ? (
          /* Map View */
          <div>
            <div
              ref={overviewMapRef}
              style={{
                height: 600,
                width: "100%",
                borderRadius: 8,
                border: "1px solid var(--border)",
                marginBottom: 16
              }}
            />
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '12px',
              background: 'var(--card-bg)',
              borderRadius: '6px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: 16, 
                  height: 16, 
                  borderRadius: '50%', 
                  backgroundColor: '#8b5cf6',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
                <span style={{ fontSize: '14px' }}>Active ({filteredUnits.filter(u => u.isActive).length})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: 16, 
                  height: 16, 
                  borderRadius: '50%', 
                  backgroundColor: '#9ca3af',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
                <span style={{ fontSize: '14px' }}>Inactive ({filteredUnits.filter(u => !u.isActive).length})</span>
              </div>
            </div>
          </div>
        ) : filteredUnits.length === 0 ? (
          <p style={{ opacity: 0.7 }}>No units match your search.</p>
        ) : (
          /* List View */
          <div style={{ display: "grid", gap: 12 }}>
            {filteredUnits.map(unit => (
              <div
                key={unit.id}
                style={{
                  padding: 16,
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  background: "var(--card-bg)"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <h4 className="bold" style={{ margin: 0 }}>
                        {unit.unitCode}
                      </h4>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: unit.isActive ? '#8b5cf6' : '#9ca3af',
                        color: 'white'
                      }}>
                        {unit.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    {unit.locationName && (
                      <div style={{ fontSize: 14, color: "var(--text-muted)" }}>{unit.locationName}</div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn"
                      onClick={() => openInMaps(unit.latitude, unit.longitude, unit.locationName)}
                      title="View on map"
                    >
                      <FiMapPin size={16} />
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleEditUnit(unit)}
                      title="Edit unit"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(unit.unitCode)}
                      title="Delete unit"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>

                <div style={{ fontSize: 13, marginTop: 8, display: "grid", gap: 4 }}>
                  <div>
                    <strong>Coordinates:</strong> {unit.latitude.toFixed(6)}, {unit.longitude.toFixed(6)}
                  </div>
                  {unit.formOrgName && (
                    <div>
                      <strong>Linked Form:</strong> {unit.formOrgName}
                    </div>
                  )}
                  {unit.linkedPartners && unit.linkedPartners.length > 0 && (
                    <div>
                      <strong>Linked Partners:</strong> {unit.linkedPartners.map(p => p.name).join(', ')}
                    </div>
                  )}
                  {unit.notes && (
                    <div>
                      <strong>Notes:</strong> {unit.notes}
                    </div>
                  )}
                  <div style={{ opacity: 0.6, fontSize: 12 }}>
                    Registered: {new Date(unit.registeredAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Unit Modal */}
      {editingUnit && (
        <div 
          className="modal-overlay" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setEditingUnit(null)}
        >
          <div 
            className="modal-content"
            style={{
              background: 'var(--card-bg)',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: 16 }}>Edit Unit</h2>
            
            <div className="form-group">
              <label>Unit Code</label>
              <input
                type="text"
                value={editingUnit.unitCode}
                disabled
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
              />
              <small style={{ fontSize: 12, opacity: 0.7 }}>Unit code cannot be changed</small>
            </div>

            <div className="form-group">
              <label>Assign to QR Form (Optional)</label>
              <select
                value={editingUnit.formId || ''}
                onChange={(e) => setEditingUnit({ ...editingUnit, formId: e.target.value || null })}
              >
                <option value="">-- No form assigned --</option>
                {myForms.map(form => (
                  <option key={form.id} value={form.id}>
                    {form.orgName || `Form #${form.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Location Name</label>
              <input
                type="text"
                value={editingUnit.locationName || ''}
                onChange={(e) => setEditingUnit({ ...editingUnit, locationName: e.target.value })}
                placeholder="e.g., Main Entrance, Conference Room A"
              />
            </div>

            <div className="form-group">
              <label>Location Coordinates</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <label style={{ fontSize: 12 }}>Latitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={editingUnit.latitude}
                    onChange={(e) => setEditingUnit({ ...editingUnit, latitude: e.target.value })}
                    placeholder="37.7749"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12 }}>Longitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={editingUnit.longitude}
                    onChange={(e) => setEditingUnit({ ...editingUnit, longitude: e.target.value })}
                    placeholder="-122.4194"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={editingUnit.notes || ''}
                onChange={(e) => setEditingUnit({ ...editingUnit, notes: e.target.value })}
                placeholder="Additional information about this unit..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editingUnit.isActive ?? true}
                  onChange={(e) => setEditingUnit({ ...editingUnit, isActive: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span>Unit is Active</span>
              </label>
              <small style={{ fontSize: 12, opacity: 0.7, display: 'block', marginTop: 4 }}>
                Active units are shown in purple on the map, inactive units in grey
              </small>
            </div>

            <div className="form-group">
              <label>Linked Partners</label>
              <Select
                isMulti
                value={selectedPartnerOptions}
                onChange={handlePartnerSelectChange}
                options={partnerOptions}
                placeholder="Select partners..."
                classNamePrefix="react-select"
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: '#4f97ff',
                    primary75: '#4f97ffbf',
                    primary50: '#4f97ff80',
                    primary25: '#4f97ff40',
                  },
                })}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: 'var(--input-bg, #fff)',
                    borderColor: state.isFocused ? '#4f97ff' : 'var(--border, #ddd)',
                    boxShadow: state.isFocused ? '0 0 0 1px #4f97ff' : 'none',
                    minHeight: '38px',
                    fontSize: '13px',
                    '&:hover': {
                      borderColor: '#4f97ff'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: 'var(--card-bg, #fff)',
                    border: '1px solid var(--border, #ddd)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 9999
                  }),
                  menuList: (base) => ({
                    ...base,
                    padding: '4px'
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected 
                      ? '#4f97ff' 
                      : state.isFocused 
                        ? 'var(--hover-bg, #f0f0f0)' 
                        : 'transparent',
                    color: state.isSelected ? '#fff' : 'var(--text, #333)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    ':active': {
                      backgroundColor: '#4f97ff'
                    }
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#4f97ff',
                    borderRadius: '4px'
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#fff',
                    fontSize: '12px',
                    padding: '3px 6px'
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: '#fff',
                    cursor: 'pointer',
                    ':hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#fff'
                    }
                  }),
                  input: (base) => ({
                    ...base,
                    color: 'var(--text, #333)',
                    margin: 0,
                    padding: 0
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: 'var(--text-muted, #999)',
                    fontSize: '13px'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: 'var(--text, #333)'
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '2px 8px'
                  })
                }}
              />
              <small style={{ fontSize: 12, opacity: 0.7, display: 'block', marginTop: 4 }}>
                Select one or more partners to link this box to
              </small>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setEditingUnit(null);
                  setSelectedPartnerIds([]);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleUpdate(editingUnit)}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ONEboxManager;

